import { MAX_CONSOLE_LOG_BYTES } from './constants';

export type ConsoleLogLevel = 'error' | 'warn';

export interface ConsoleLogEntry {
  timestamp: string;
  level: ConsoleLogLevel;
  message: string;
}

/**
 * Emitted at the head of the log whenever the size guard has dropped older
 * entries, so a reader knows the capture is not the full session.
 */
const TRUNCATION_MARKER_LINE =
  '[feedback] NOTE: console log truncated -- oldest entries were dropped to stay within the size cap.';

/** A joined log line is terminated by a single newline; counted toward the cap. */
const NEWLINE_BYTES = 1;

const encoder = new TextEncoder();

const buffer: ConsoleLogEntry[] = [];
let bufferBytes = 0;
let truncated = false;
let installed = false;

function serializeArg(arg: unknown): string {
  if (arg instanceof Error) {
    const head = `${arg.name}: ${arg.message}`;
    return arg.stack ? `${head}\n${arg.stack}` : head;
  }
  if (typeof arg === 'object' && arg !== null) {
    const seen = new WeakSet<object>();
    try {
      return JSON.stringify(arg, (_key, value: unknown) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) return '[Circular]';
          seen.add(value);
        }
        return value;
      });
    } catch {
      // Values JSON cannot represent (e.g. BigInt) fall through to String() per
      // the safe-serialization contract; the log entry is still recorded.
      return String(arg);
    }
  }
  return String(arg);
}

function formatEntry(entry: ConsoleLogEntry): string {
  return `[${entry.timestamp}] ${entry.level.toUpperCase()}: ${entry.message}`;
}

function lineBytes(entry: ConsoleLogEntry): number {
  return encoder.encode(formatEntry(entry)).length + NEWLINE_BYTES;
}

function enforceCap(): void {
  while (bufferBytes > MAX_CONSOLE_LOG_BYTES && buffer.length > 0) {
    const removed = buffer.shift();
    if (removed) bufferBytes -= lineBytes(removed);
    truncated = true;
  }
}

function appendEntry(level: ConsoleLogLevel, args: unknown[]): void {
  const entry: ConsoleLogEntry = {
    timestamp: new Date().toISOString(),
    level,
    message: args.map(serializeArg).join(' '),
  };
  buffer.push(entry);
  bufferBytes += lineBytes(entry);
  enforceCap();
}

/**
 * Wrap `console.error`/`console.warn` and listen for uncaught errors and
 * rejected promises, appending each to an in-memory session buffer. Idempotent:
 * repeated calls are no-ops. Must run at app bootstrap, before other code logs.
 */
export function installConsoleLogCapture(): void {
  if (installed) return;
  installed = true;

  const originalError = console.error.bind(console);
  const originalWarn = console.warn.bind(console);

  console.error = (...args: unknown[]): void => {
    appendEntry('error', args);
    originalError(...args);
  };
  console.warn = (...args: unknown[]): void => {
    appendEntry('warn', args);
    originalWarn(...args);
  };

  window.addEventListener('error', event => {
    appendEntry('error', [event.error ?? event.message]);
  });
  window.addEventListener('unhandledrejection', event => {
    appendEntry('error', ['Unhandled promise rejection:', event.reason]);
  });
}

/**
 * Serialize the captured session as newline-delimited lines, prefixed with a
 * truncation marker when the guard has dropped entries. The result is
 * guaranteed to stay within the server's console-log byte cap.
 */
export function getConsoleLog(): string {
  const lines = buffer.map(formatEntry);
  if (truncated) lines.unshift(TRUNCATION_MARKER_LINE);

  const firstEntryIndex = truncated ? 1 : 0;
  const sizes = lines.map(line => encoder.encode(line).length + NEWLINE_BYTES);
  let total = sizes.reduce((sum, size) => sum + size, 0);
  let start = firstEntryIndex;
  while (total > MAX_CONSOLE_LOG_BYTES && lines.length - start > 1) {
    total -= sizes[start];
    start += 1;
  }
  const kept = truncated ? [lines[0], ...lines.slice(start)] : lines.slice(start);
  return kept.join('\n');
}

/** Test hook: clear the singleton buffer so cases start from a known state. */
export function resetConsoleLogBuffer(): void {
  buffer.length = 0;
  bufferBytes = 0;
  truncated = false;
}
